import React from "react";
import {useTranslation} from "react-i18next";
import {Controller, useFormContext} from "react-hook-form";
import TextField from "@mui/material/TextField";

import {ADORNMENTS} from "../../../../../../../../../../../config/constants";
import {CERTIFICATION_AUTO_FORM, CertificationFormType} from "../../../../index";
import {checkDecimalsAfterDot} from "../../../../../../../../../../../utils/validation";
import {setBrokerDataMutation, useLazyGetBrokerDataByCodeQuery} from "../../../../../../../../../../../api/calc";
import {
  useFormattedDecimalInput,
  useGetCalcIdFromRoute,
} from "../../../../../../../../../../../utils/hooks";
import {BROKER_ACTIONS} from "../../../../../../../../../../../utils/constants";
import {isCalculationViewModeError} from 'utils/helpers';
import {useCalculationReadOnly} from '../../../../../../../../calculation-read-only';

type Props = {
    index: number
    code: string
    country: string
}

const CustomCertification = ({index, code, country} : Props) => {

  const orderId = useGetCalcIdFromRoute()

  const { t } = useTranslation();
  const readOnly = useCalculationReadOnly();

  const { control, getValues } = useFormContext<CertificationFormType>();

  const [ setBrokerData ] = setBrokerDataMutation()

  const [ triggerGetBrokerDataByCode ] = useLazyGetBrokerDataByCodeQuery();

  const errorNumber = t('errors__message__incorrectValueTwoDecimalsAfterComma');

  const fieldName = `codes.${index}.${CERTIFICATION_AUTO_FORM.COST_TOTAL_MANUAL}` as const;

  const decimalInput = useFormattedDecimalInput();

  const handleBlur = async () => {
    if (readOnly) {
      return;
    }

    const cost = getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.COST_TOTAL_MANUAL];

    if (!cost) return;

    const result = await setBrokerData({
      orderId,
      code: code,
      has_certificate: 'false',
      cost_total_manual: cost,
      countryCode: country,
      action: BROKER_ACTIONS.CERT_MANUAL,
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    await triggerGetBrokerDataByCode(orderId);

  }

  return <Controller
    control={control}
    name={fieldName}
    rules={{
      validate: (value) => value ? checkDecimalsAfterDot(value, errorNumber): true,
      min: { value: 0, message: errorNumber },
    }}
    render={({ field, fieldState }) => (
      <TextField
        disabled={readOnly}
        value={decimalInput.getDisplayValue({
          fieldName: fieldName,
          rawValue: field.value,
        })}
        onChange={(e) => {
          decimalInput.startEditing(fieldName);
          const value = e.target.value;
          const valueWithDot = value.replace(',', '.');
          field.onChange(valueWithDot);
        }}
        onBlur={() => {
          field.onBlur();
          decimalInput.stopEditing(fieldName);
          handleBlur();
        }}
        sx={{
          marginTop: 3,
          maxWidth: 306,
        }}
        error={!!fieldState.error}
        helperText={fieldState.error?.message}
        variant='outlined'
        label={`${t('dictionaries__text__certification__сostOfCertification')}`}
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{ endAdornment: ADORNMENTS.PRICE }}
      />
    )}
  />
}

export default CustomCertification
